import * as three from 'three';
import { Space2DInterface } from '../../lib/space';
import { a3 } from '@lukekaalim/act-three';
import { Component, Element, h, Node, useEffect, useMemo, useRef, useState } from '@lukekaalim/act';
import { integerCoordinates, iterateChunkDataCoords } from '../../lib/chunks';
import { Logger, Map2D, useIsDebug, useJeffect, useLogger, useRefresh } from '@kosmod/kit';
import { TerrainSystemData, useChunkMapKeys } from './terrain';
import { filter, Observable } from 'rxjs';
import { ObjectClone, TileMaterial, useDisposable } from '@kosmod/graphics-3d';
import { TheatreState } from '../../hooks/useTheatreState';
import { CanvasCoordinator, ReadonlyChunkMap } from '@kosmod/stew';
import { TerrainBrush, TerrainBrushID, TerrainCanvas } from '../../defs';
import { Shape } from '@kosmod/pancake';

export type DOISTerrainProps = {
  theatre: TheatreState,

  canvas: TerrainCanvas,

  terrain: ReadonlyChunkMap<null | TerrainSystemData>,
  visibility: ReadonlyChunkMap<number>,

  coordinator: CanvasCoordinator,

  material?: TileMaterial,
};

export const DOISTerrain: Component<DOISTerrainProps> = ({ terrain, canvas, visibility, material, coordinator, theatre }) => {
  const logger = useLogger('DOISTerrain').createSubsystem('Canvas');
  
  const chunks = useChunkMapKeys(terrain, coordinator);

  return useMemo(() => {
    // logger.log(`DrawSystem`)
    return [
      h(a3.directionalLight, { position: { x: 1, y: 10, z: 1.5 }, color: new three.Color('#fff') }),
      h(a3.directionalLight, { position: { x: -2, y: 10, z: -0.5 }, color: new three.Color('#fff') }),
      h(a3.ambientLight, { color: new three.Color('#2b2b2b') }),

      chunks.map(([chunkX, chunkY]) => h(DeadSimpleTerrainChunk, {
        key: [chunkX, chunkY].join(':'),
        theatre,
        terrain,
        canvas,
        coordinator,

        chunkX: chunkX,
        chunkY: chunkY,
        material,
        visibility,
      }))
    ]
  }, [coordinator, material, theatre])

};

type DeadSimpleTerrainChunkProps = DOISTerrainProps & {
  chunkX: number,
  chunkY: number,
};


class UnlitChunkMaterial extends TileMaterial {
  fragmentShader = `
#include <common>
#include <color_pars_fragment>
#include <lights_pars_begin>
#include <normal_pars_fragment>
#include <lights_physical_pars_fragment>

uniform vec3 diffuse;
uniform vec3 vis_color;
uniform float roughness;
uniform float metalness;
uniform mat4 modelMatrix;

uniform vec2 tilePosition;
uniform vec2 chunkPos;
uniform sampler2D data;

varying vec3 vViewPosition;

void main() {
	vec4 diffuseColor = vec4( diffuse, 1.0 );
	ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
	vec3 totalEmissiveRadiance = vec3(0.0, 0.0, 0.0);

	#include <map_fragment>
	#include <color_fragment>
	#include <roughnessmap_fragment>
	#include <metalnessmap_fragment>
	#include <normal_fragment_begin>
	#include <normal_fragment_maps>
	#include <lights_physical_fragment>
	#include <lights_fragment_begin>
	#include <lights_fragment_maps>
	#include <lights_fragment_end>
	#include <transmission_fragment>
	vec3 totalDiffuse = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse;
	vec3 totalSpecular = reflectedLight.directSpecular + reflectedLight.indirectSpecular;
	vec3 outgoingLight = totalDiffuse + totalSpecular + totalEmissiveRadiance;

	gl_FragColor = vec4( outgoingLight, 1.0 );
  
  vec2 samplePos = (tilePosition / 8.0) - chunkPos;
  //gl_FragColor = diffuseColor;

  //gl_FragColor.rg = samplePos;
  float tileLight = texture(data, samplePos).r;

	gl_FragColor.rgb *= min(tileLight * 50.0, 1.0) * 0.8;
}
  `
  vertexShader = `
#include <common>
#include <color_pars_vertex>
#include <uv_pars_vertex>
#include <normal_pars_vertex>

uniform sampler2D data;
uniform vec2 chunkPos;

uniform bool hideInvisible;
varying vec3 vViewPosition;

void main() {
	#include <uv_vertex>
	#include <color_vertex>
	#include <beginnormal_vertex>
	#include <defaultnormal_vertex>
	#include <normal_vertex>
	#include <begin_vertex>
	#include <project_vertex>
	#include <worldpos_vertex>

  vec2 worldPos = floor(modelMatrix[3].xz) / 8.0;
  vec2 samplePos = worldPos - chunkPos;
  float visibility = texture(data, samplePos).r;

  if (hideInvisible && visibility < 0.0001) {
    gl_Position = vec4(-10, -10, -10, -10);
  }

	vViewPosition = - mvPosition.xyz;
}
  `;
  lights = true;

  defines = {
    USE_COLOR: true,
  }

  uniforms = {
    ...three.UniformsUtils.clone(three.ShaderLib.standard.uniforms),

    diffuse: new three.Uniform(new three.Color('white')),
    data: new three.Uniform(new three.Texture()),
    chunkPos: new three.Uniform(new three.Vector2()),
    hideInvisible: new three.Uniform(false),
    vis_color: new three.Uniform(new three.Color('red')),

    tilePosition: new three.Uniform(new three.Vector2()),
  }

  onBeforeRender(
    renderer: three.WebGLRenderer, scene: three.Scene,
    camera: three.Camera, geometry: three.BufferGeometry,
    object: three.Object3D, group: three.Group
  ): void {
    
  }
}

const DeadSimpleTerrainChunk: Component<DeadSimpleTerrainChunkProps> = ({
  canvas,
  terrain,
  visibility,
  coordinator,
  theatre,

  chunkX, chunkY,
  material, 
}) => {
  const box = new three.Box2(
    new three.Vector2(chunkX, chunkY).multiplyScalar(8),
    new three.Vector2(chunkX + 1, chunkY + 1).multiplyScalar(8),
  )

  const logger = useLogger('DOISTerrain').createSubsystem('Chunk')
  const [refreshMesh, meshKey] = useRefresh()
  const [refreshVisibility, visKey] = useRefresh()

  const readyRef = useRef(false);

  useJeffect(j => {
    logger.log(`Chunk initializing`, chunkX, chunkY)

    j.add(coordinator.updates.subscribe((chunks) => {
      let meshNeedsUpdate = false;
      let visibilityNeedsUpdate = false;

      for (const [[canvas, x, y]] of chunks) {
        if (x === chunkX && y === chunkY) {
          if (canvas === visibility.canvasId)
            visibilityNeedsUpdate = true;
          if (canvas === terrain.canvasId)
            meshNeedsUpdate = true;
        }
      }
      if (meshNeedsUpdate)
        refreshMesh();
      if (visibilityNeedsUpdate)
        refreshVisibility()
    }));

    // only render after we subscribe
    // otherwise there weird edge cases where a change happens
    // after first render but before we subscribe to changes
    readyRef.set(true);
    refreshMesh();
    refreshVisibility();
  }, [coordinator, chunkX, chunkY])

  const isDebug = useIsDebug();


  const visibilityBuffer = useRef(() => new Uint8Array(8 * 8)).get()
  const data = useDisposable(() => {
    return new three.DataTexture(visibilityBuffer, 8, 8,
      three.RedFormat,
      three.UnsignedByteType,
      three.Texture.DEFAULT_MAPPING,
      three.ClampToEdgeWrapping, three.ClampToEdgeWrapping
    );
  }, [visibilityBuffer])

  useEffect(() => {
    if (!readyRef.get())
      return;

    const godVision = theatre.data.players.findIndex(p => p.id === theatre.data.self.id) === 0;

    for (const [x, y, i] of iterateChunkDataCoords()) {
      let vis = visibility.read(x + (chunkX * 8), y + (chunkY * 8))
      if (godVision)
        vis = Math.max(1, vis);

      visibilityBuffer[i] = vis;
    }
    logger.log(`Updating chunk vis texture`, chunkX, chunkY)
    data.needsUpdate = true;
  }, [visKey])

  const chunkBounds = useMemo(() => new three.Box3(
      new three.Vector3(chunkX * 8, 0, chunkY * 8),
      new three.Vector3((chunkX + 1) * 8, 0, (chunkY + 1) * 8),
    )
    .expandByVector(new three.Vector3(-.05, 0, -.05)),
    [chunkX, chunkY]
  )

  const objects = useMemo(() => {
    if (!readyRef.get())
      return [];

    const material = new UnlitChunkMaterial();
    material.uniforms.data.value = data;
    data.needsUpdate = true;
    material.uniforms.chunkPos.value.x = chunkX;
    material.uniforms.chunkPos.value.y = chunkY;
    material.uniforms.hideInvisible.value = theatre.data.players.findIndex(p => p.id === theatre.data.self.id) !== 0;

    const objects: Node[] = [];

    
    logger.log(`Updating Chunk Mesh`, chunkX, chunkY)
    for (const coordinate of integerCoordinates(box)) {
      const renderBrushPart = (
        part: keyof TerrainBrush['partNames'],
        brushID: null | TerrainBrushID,
        position: three.Vector3 = new three.Vector3(),
        rotation: three.Euler = new three.Euler(),
      ) => {
        if (!brushID)
          return null;
        
        const asset = theatre.sceneRes.brushParts.get(brushID);
        if (!asset || !asset[part])
          return null;

        objects.push(h(ObjectClone, { target: asset[part], position, rotation, material, tilePosition: coordinate }))
      }

      const data = terrain.read(coordinate.x, coordinate.y);
      
      if (data) {
        if (data.floor) {
          renderBrushPart('floor_4', data.floor, new three.Vector3(coordinate.x + .5, 0, coordinate.y + .5));
        }
        if (data.walls[0]) {
          const brush = theatre.data.brushes.get(data.walls[0]);
          if (brush && brush.renderMode === 'beta') {
            const wallData: TerrainSystemWallData = constructWallData(data, data.walls[0], 0, logger);
            const asset = theatre.sceneRes.brushParts.get(wallData.self)!;
            objects.push(
              renderWallTerrainPart(
                coordinate.clone(),
                wallData, asset.wall_1!, [],
                new three.Vector3(coordinate.x, 0, coordinate.y).addScalar(.5),
                null,
                material,
              )
            )
          } else {
            if (data.neighborFloors[0] && !data.floor) {
              renderBrushPart('wall_1', data.walls[0], new three.Vector3(coordinate.x + .5, .5, coordinate.y), new three.Euler(0, Math.PI * 1, 0));
            } else if (!data.neighborFloors[0] && data.floor) {
              renderBrushPart('wall_1', data.walls[0], new three.Vector3(coordinate.x + .5, .5, coordinate.y), new three.Euler(0, Math.PI * 0, 0));
            } else {
              renderBrushPart('wall_1', data.walls[0], new three.Vector3(coordinate.x + .5, .5, coordinate.y), new three.Euler(0, Math.PI * 1, 0));
            }
          }
        }
        if (data.walls[1]) {
          const brush = theatre.data.brushes.get(data.walls[1]);
          if (brush && brush.renderMode === 'beta') {
            const wallData: TerrainSystemWallData = constructWallData(data, data.walls[1], 1, logger);
            const asset = theatre.sceneRes.brushParts.get(wallData.self)!;
            objects.push(
              renderWallTerrainPart(
                coordinate.clone(),
                wallData, asset.wall_1!, [],
                new three.Vector3(coordinate.x, 0, coordinate.y).addScalar(.5),
                new three.Euler(0, Math.PI * 1.5, 0),
                material,
              )
            )
          }
        }
        if (data.walls[2]) {
          const brush = theatre.data.brushes.get(data.walls[2]);
          if (brush && brush.renderMode === 'beta') {
            const wallData: TerrainSystemWallData = constructWallData(data, data.walls[2], 2, logger);
            const asset = theatre.sceneRes.brushParts.get(wallData.self)!;
            objects.push(
              renderWallTerrainPart(
                coordinate.clone(),
                wallData, asset.wall_1!, [],
                new three.Vector3(coordinate.x, 0, coordinate.y).addScalar(.5),
                new three.Euler(0, Math.PI * 1, 0),
                material,
              )
            )
          }
        }

        if (data.walls[3]) {
          const brush = theatre.data.brushes.get(data.walls[3]);
          if (brush && brush.renderMode === 'beta') {
            const wallData: TerrainSystemWallData = constructWallData(data, data.walls[3], 3, logger);

            const asset = theatre.sceneRes.brushParts.get(wallData.self)!;
            objects.push(
              renderWallTerrainPart(
                coordinate.clone(),
                wallData, asset.wall_1!, [],
                new three.Vector3(coordinate.x, 0, coordinate.y).addScalar(.5),
                new three.Euler(0, Math.PI * .5, 0),
                material,
              )
            )
          } else {
            if (data.neighborFloors[3] && !data.floor) {
              renderBrushPart('wall_1', data.walls[3], new three.Vector3(coordinate.x, .5, coordinate.y + .5), new three.Euler(0, Math.PI * 1.5, 0));
            } else if (!data.neighborFloors[3] && data.floor) {
              renderBrushPart('wall_1', data.walls[3], new three.Vector3(coordinate.x, .5, coordinate.y + .5), new three.Euler(0, Math.PI * .5, 0));
            } else {
              renderBrushPart('wall_1', data.walls[3], new three.Vector3(coordinate.x, .5, coordinate.y + .5), new three.Euler(0, Math.PI * 1.5, 0));
            }
          }
        }
      }
    }
    // logger.log(objects)
    
    return objects;
  }, [meshKey])

  return h(a3.group, {}, [
    objects,
    isDebug && h(a3.Box3Helper, {
      box: chunkBounds,
      material: new three.LineBasicMaterial({
        color: new three.Color().setHSL(Math.random(), .5, .5),
        toneMapped: false
      })
    })
  ])
};

export const USERDATA_PROPS = {
  OPTS: 'kosmod:opts',
  CONDITION: 'kosmod:condition',
}

export const OPTS_KEYWORDS = {
  ignoreTransform: 'ignore-transform'
}

export const DEFAULT_OPTS = {
  ignoreTransform: false,
}

export const readOpts = (userDataValue: unknown) => {
  if (!userDataValue || typeof userDataValue !== 'string')
    return DEFAULT_OPTS;

  const optKeywords = userDataValue.split(',');
  
  return {
    ignoreTransform: optKeywords.includes(OPTS_KEYWORDS.ignoreTransform)
  }
}

export const constructWallData = (
  data: TerrainSystemData,
  self: TerrainBrushID,
  directionIndex: number, // 0=UP, 1=RIGHT, 2=DOWN, 3=LEFT
  logger: Logger
): TerrainSystemWallData => {

  return {
    self: self,
    logger: logger.createSubsystem(`Wall[${directionIndex}]`),
    innerFloor: data.floor,
    outerFloor: data.neighborFloors[directionIndex],
    neighbors: [
      data.neighborWalls[directionIndex][0],
      data.neighborWalls[directionIndex][1],
      data.neighborWalls[(directionIndex + 1) % 4][0],
      data.walls[(directionIndex + 1) % 4],
      data.walls[(directionIndex + 3) % 4],
      data.neighborWalls[(directionIndex + 3) % 4][1],
    ]
  }
}

export const CONDITION_PATTERN_VALUES = {
  ANY: '*',
  NONE: '0',
  SELF: '+',
  SOMEONE: '1',
} as const;
export type CONDITION_PATTERN_VALUE = typeof CONDITION_PATTERN_VALUES[keyof typeof CONDITION_PATTERN_VALUES]

export type Tuple6<T> = [T, T, T, T, T, T]

export type Condition =
  | { type: 'none' }
  | { type: 'default' }
  | { type: 'wall', pattern: Tuple6<CONDITION_PATTERN_VALUE> }

export type TerrainSystemWallData = {
  self: TerrainBrushID,

  innerFloor: TerrainBrushID | null,
  outerFloor: TerrainBrushID | null,
  neighbors: Tuple6<TerrainBrushID | null>,
  logger: Logger,
}

export const readCondition = (userDataValue: unknown): Condition => {
  if (!userDataValue || typeof userDataValue !== 'string')
    return { type: 'none' };

  if (userDataValue === 'default')
    return { type: 'default' };

  if (userDataValue.startsWith('wall(')) {
    let values = userDataValue.slice('wall('.length, -(')'.length))
    return {
      type: 'wall',
      pattern: [...values] as Tuple6<CONDITION_PATTERN_VALUE>
    }
  }
  return { type: 'none' }
}

export const validateWallCondition = (data: TerrainSystemWallData, condition: Condition, siblings: three.Object3D[]): boolean => {
  if (condition.type === 'default') {
    // find all siblings with non-default or none-none conditions
    const siblingConditions = siblings
      .map(s => readCondition(s.userData[USERDATA_PROPS.CONDITION]))
      .filter(c => c.type !== 'none' && c.type !== 'default');

    return !siblingConditions.some(condition => validateWallCondition(data, condition, []));
  }
  if (condition.type === 'none')
    return true;
  if (condition.type === 'wall') {
    const result = computePattern(condition.pattern, data.neighbors, data.self);
    data.logger.log(condition.pattern, result, data.neighbors)
    return !result.some(v => !v);
  }
  // some mystery condition
  return false;
}
const computePattern = (
  wallPattern: Tuple6<CONDITION_PATTERN_VALUE>,
  data: Tuple6<TerrainBrushID | null>,
  self: TerrainBrushID
) => {
  return wallPattern.map((pattern, i) => {
      // const pattern = condition.pattern[i];
    const wallValue = data[i];

    switch (pattern) {
      case CONDITION_PATTERN_VALUES.ANY:
        return true;
      case CONDITION_PATTERN_VALUES.NONE:
        if (wallValue !== null)
          return false
        return true;
      case  CONDITION_PATTERN_VALUES.SELF:
        if (wallValue !== self)
          return false;
        return true;
      case CONDITION_PATTERN_VALUES.SOMEONE:
        if (wallValue === null)
          return false;
        return true;
    }
  });
}

export const renderWallTerrainPart = (
  tilePosition: three.Vector2,
  data: TerrainSystemWallData,
  part: three.Object3D,
  siblings: three.Object3D[] = [],
  forcePosition?: three.Vector3 | null,
  forceRotation?: three.Euler | null,
  forceMaterial?: three.Material | null,
): Node => {
  const meta = readOpts(part.userData[USERDATA_PROPS.OPTS])
  const condition = readCondition(part.userData[USERDATA_PROPS.CONDITION]);

  const valid = validateWallCondition(data, condition, siblings) && (data.innerFloor || (!data.innerFloor && !data.outerFloor));
  
  data.logger.createSubsystem(part.name).log(condition, valid)
  if (!valid)
    return null;

  let position = forcePosition || (meta.ignoreTransform ? new three.Vector3() : part.position);
  let rotation = forceRotation || (meta.ignoreTransform ? new three.Euler() : part.rotation);
  
  let children = part.children
    .map(childPart => renderWallTerrainPart(tilePosition, data, childPart, part.children.filter(c => c.id !== part.id), null, null, forceMaterial));

  if (part instanceof three.Mesh) {
    data.logger.createSubsystem(part.name).log('Rendering mesh');
    return h(a3.tile, {
      position,
      rotation,
      geometry: part.geometry,
      material: forceMaterial || part.material,
      scale: part.scale,
      tilePosition,
    }, children)
  }
  if (part instanceof three.Group || part instanceof three.Object3D) {
    data.logger.createSubsystem(part.name).log('Rendering Group');
    return h(a3.group, {
      position,
      rotation,
      scale: part.scale,
    }, children)
  }

  data.logger.log('Rendering nothing?', part);
  return null;
}